import { Plus, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "./Textarea";
import {
	SWIM_TYPES,
	SWIM_STROKES,
	nextEnduranceName,
} from "../../lib/programBuilderUtils";
import { EnduranceNameField } from "./EnduranceNameField";

/**
 * Swim workout configuration form. Extracted verbatim from the ProgramBuilder
 * "Add Exercise" dialog. All state lives in the parent; behavior is identical.
 */
export function SwimForm({ newExercise, setNewExercise, benchmarkOptions, t }) {
	return (
		<div className="space-y-3">
			{/* Swim type selector */}
			<div className="space-y-2">
				<Label className="label-tactical">{t("pbSwimTypeLabel")}</Label>
				<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
					{SWIM_TYPES.map((st) => (
						<button
							key={st.value}
							onClick={() => {
								const updates = {
									...newExercise,
									swim_type: st.value,
									exercise_name: nextEnduranceName(
										newExercise.exercise_name,
										`Swim - ${st.label}`,
									),
								};
								if (
									["intervals", "threshold", "sprint", "drill"].includes(
										st.value,
									) &&
									(!newExercise.swim_sets ||
										newExercise.swim_sets.length === 0)
								) {
									updates.swim_sets = [
										{
											repetitions: 1,
											distance: "",
											target_time: "",
											rest: "",
											stroke: "",
											equipment: "",
											intensity: "",
											notes: "",
										},
									];
								}
								setNewExercise(updates);
							}}
							className={`p-2 rounded-sm border text-xs text-center transition-colors ${
								newExercise.swim_type === st.value
									? "border-cyan-400 bg-cyan-400/10 text-cyan-400"
									: "border-border text-muted-foreground hover:border-cyan-400/50"
							}`}
						>
							{t(st.labelKey)}
						</button>
					))}
				</div>
			</div>

			{newExercise.swim_type && (
				<EnduranceNameField
					newExercise={newExercise}
					setNewExercise={setNewExercise}
					t={t}
				/>
			)}

			{/* Warmup — for all swim types except custom */}
			{newExercise.swim_type &&
				newExercise.swim_type !== "custom" &&
				newExercise.swim_type !== "benchmark" && (
					<div className="space-y-1">
						<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
							{t("warmup")}
						</span>
						<Input
							value={newExercise.warmup_description ?? ""}
							onChange={(e) =>
								setNewExercise({
									...newExercise,
									warmup_description: e.target.value,
								})
							}
							className="bg-secondary border-transparent rounded-sm text-xs"
							placeholder={t("pbSwimWarmupPlaceholder")}
						/>
					</div>
				)}

			{/* === INTERVALS / THRESHOLD / SPRINT / DRILL: Multi-row swim sets === */}
			{["intervals", "threshold", "sprint", "drill"].includes(
				newExercise.swim_type,
			) && (
				<div className="space-y-2">
					<div className="flex items-center justify-between">
						<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
							{t("sets")}
						</span>
						<Button
							variant="ghost"
							size="sm"
							onClick={() =>
								setNewExercise({
									...newExercise,
									swim_sets: [
										...(newExercise.swim_sets || []),
										{
											repetitions: 1,
											distance: "",
											target_time: "",
											rest: "",
											stroke: "",
											equipment: "",
											intensity: "",
											notes: "",
										},
									],
								})
							}
							className="h-6 px-2 text-cyan-400 hover:text-cyan-300"
						>
							<Plus className="w-3 h-3 mr-1" /> {t("sets")}
						</Button>
					</div>
					{(newExercise.swim_sets || []).map((row, ri) => (
						<div key={ri} className="bg-secondary/50 rounded-sm p-3 space-y-2">
							<div className="flex items-center justify-between">
								<span className="text-xs font-medium text-cyan-400">
									Serie {ri + 1}
								</span>
								<button
									onClick={() => {
										const rows = [...(newExercise.swim_sets || [])];
										rows.splice(ri, 1);
										setNewExercise({
											...newExercise,
											swim_sets: rows,
										});
									}}
									className="text-muted-foreground hover:text-destructive"
								>
									<Trash2 className="w-3 h-3" />
								</button>
							</div>
							<div className="grid grid-cols-3 gap-2">
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("reps")}
									</span>
									<Input
										type="text"
										inputMode="numeric"
										value={row.repetitions ?? ""}
										onFocus={(e) => e.target.select()}
										onChange={(e) => {
											const rows = [...(newExercise.swim_sets || [])];
											rows[ri] = {
												...rows[ri],
												repetitions:
													e.target.value === ""
														? ""
														: Number(e.target.value),
											};
											setNewExercise({
												...newExercise,
												swim_sets: rows,
											});
										}}
										className="bg-secondary border-transparent rounded-sm text-xs"
										placeholder="4"
									/>
								</div>
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("distanceLabel")}
									</span>
									<Input
										value={row.distance ?? ""}
										onChange={(e) => {
											const rows = [...(newExercise.swim_sets || [])];
											rows[ri] = {
												...rows[ri],
												distance: e.target.value,
											};
											setNewExercise({
												...newExercise,
												swim_sets: rows,
											});
										}}
										className="bg-secondary border-transparent rounded-sm text-xs"
										placeholder="100m"
									/>
								</div>
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("pbTargetTime")}
									</span>
									<Input
										value={row.target_time ?? ""}
										onChange={(e) => {
											const rows = [...(newExercise.swim_sets || [])];
											rows[ri] = {
												...rows[ri],
												target_time: e.target.value,
											};
											setNewExercise({
												...newExercise,
												swim_sets: rows,
											});
										}}
										className="bg-secondary border-transparent rounded-sm text-xs"
										placeholder="1:45"
									/>
								</div>
							</div>
							<div className="grid grid-cols-2 md:grid-cols-4 gap-2">
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("recovery")}
									</span>
									<Input
										value={row.rest ?? ""}
										onChange={(e) => {
											const rows = [...(newExercise.swim_sets || [])];
											rows[ri] = {
												...rows[ri],
												rest: e.target.value,
											};
											setNewExercise({
												...newExercise,
												swim_sets: rows,
											});
										}}
										className="bg-secondary border-transparent rounded-sm text-xs"
										placeholder="30s"
									/>
								</div>
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("intensity")}
									</span>
									<Input
										value={row.intensity ?? ""}
										onChange={(e) => {
											const rows = [...(newExercise.swim_sets || [])];
											rows[ri] = {
												...rows[ri],
												intensity: e.target.value,
											};
											setNewExercise({
												...newExercise,
												swim_sets: rows,
											});
										}}
										className="bg-secondary border-transparent rounded-sm text-xs"
										placeholder="Z3, CSS+5s..."
									/>
								</div>
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("pbSwimStrokeLabel")}
									</span>
									<select
										value={row.stroke ?? ""}
										onChange={(e) => {
											const rows = [...(newExercise.swim_sets || [])];
											rows[ri] = {
												...rows[ri],
												stroke: e.target.value,
											};
											setNewExercise({
												...newExercise,
												swim_sets: rows,
											});
										}}
										className="w-full bg-secondary border-transparent rounded-sm text-xs h-9 px-2"
									>
										<option value="">—</option>
										{SWIM_STROKES.map((s) => (
											<option key={s.value} value={s.value}>
												{t(s.labelKey)}
											</option>
										))}
									</select>
								</div>
								<div className="space-y-1">
									<span className="text-[10px] text-muted-foreground">
										{t("pbSwimGearLabel")}
									</span>
									<Input
										value={row.equipment ?? ""}
										onChange={(e) => {
											const rows = [...(newExercise.swim_sets || [])];
											rows[ri] = {
												...rows[ri],
												equipment: e.target.value,
											};
											setNewExercise({
												...newExercise,
												swim_sets: rows,
											});
										}}
										className="bg-secondary border-transparent rounded-sm text-xs"
										placeholder={t("pbSwimGearSetPlaceholder")}
									/>
								</div>
							</div>
						</div>
					))}
				</div>
			)}

			{/* === ENDURANCE / TECHNIQUE / MIXED / RECOVERY: simple fields === */}
			{["endurance", "technique", "mixed", "recovery"].includes(
				newExercise.swim_type,
			) && (
				<div className="space-y-2">
					<div className="grid grid-cols-3 gap-3">
						<div className="space-y-1">
							<span className="text-xs text-muted-foreground">
								{t("distanceMetersLabel")}
							</span>
							<Input
								type="text"
								inputMode="numeric"
								value={newExercise.distance_meters ?? ""}
								onChange={(e) =>
									setNewExercise({
										...newExercise,
										distance_meters:
											e.target.value === "" ? null : e.target.value,
									})
								}
								className="bg-secondary border-transparent rounded-sm"
								placeholder="1500"
							/>
						</div>
						<div className="space-y-1">
							<span className="text-xs text-muted-foreground">
								{t("durationMin")}
							</span>
							<Input
								type="text"
								inputMode="numeric"
								value={newExercise.duration_minutes ?? ""}
								onChange={(e) =>
									setNewExercise({
										...newExercise,
										duration_minutes:
											e.target.value === "" ? null : e.target.value,
									})
								}
								className="bg-secondary border-transparent rounded-sm"
								placeholder="45"
							/>
						</div>
						<div className="space-y-1">
							<span className="text-xs text-muted-foreground">
								{t("pbSwimPacePer100")}
							</span>
							<Input
								value={newExercise.target_pace ?? ""}
								onChange={(e) =>
									setNewExercise({
										...newExercise,
										target_pace: e.target.value,
									})
								}
								className="bg-secondary border-transparent rounded-sm"
								placeholder="2:00/100m"
							/>
						</div>
					</div>
					{newExercise.swim_type === "technique" && (
						<div className="space-y-1">
							<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
								{t("pbSwimGearLabel")}
							</span>
							<Input
								value={newExercise.swim_equipment ?? ""}
								onChange={(e) =>
									setNewExercise({
										...newExercise,
										swim_equipment: e.target.value,
									})
								}
								className="bg-secondary border-transparent rounded-sm text-xs"
								placeholder={t("pbSwimGearSessionPlaceholder")}
							/>
						</div>
					)}
				</div>
			)}

			{/* === CUSTOM — free-text workout === */}
			{newExercise.swim_type === "custom" && (
				<div className="space-y-1">
					<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
						{t("workoutDescription")}
					</span>
					<Textarea
						value={newExercise.custom_workout_text ?? ""}
						onChange={(e) =>
							setNewExercise({
								...newExercise,
								custom_workout_text: e.target.value,
							})
						}
						className="bg-secondary border-transparent rounded-sm text-xs min-h-[120px] resize-y md:resize overflow-auto"
						placeholder={t("pbSwimFreePlaceholder")}
					/>
				</div>
			)}

			{/* Cooldown — for all swim types except custom */}
			{newExercise.swim_type && newExercise.swim_type !== "custom" && (
				<div className="space-y-1">
					<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
						{t("cooldown")}
					</span>
					<Input
						value={newExercise.cooldown_description ?? ""}
						onChange={(e) =>
							setNewExercise({
								...newExercise,
								cooldown_description: e.target.value,
							})
						}
						className="bg-secondary border-transparent rounded-sm text-xs"
						placeholder={t("pbSwimCooldownPlaceholder")}
					/>
				</div>
			)}

			{/* === BENCHMARK EXERCISE PICKER (Swim) === */}
			{newExercise.swim_type === "benchmark" && (
				<div className="space-y-3">
					<div className="space-y-1">
						<Label className="label-tactical">{t("pbSelectBenchmark")}</Label>
						{benchmarkOptions.swim.length > 0 ? (
							<div className="grid grid-cols-2 gap-2">
								{benchmarkOptions.swim.map((bm) => (
									<button
										key={bm.exercise_id}
										onClick={() =>
											setNewExercise({
												...newExercise,
												benchmark_exercise_id: bm.exercise_id,
												benchmark_exercise_name: bm.name,
												exercise_name: `Swim Benchmark - ${bm.name}`,
											})
										}
										className={`p-2 rounded-sm border text-xs text-left transition-colors ${
											newExercise.benchmark_exercise_id === bm.exercise_id
												? "border-cyan-400 bg-cyan-400/10 text-cyan-400"
												: "border-border text-muted-foreground hover:border-cyan-400/50"
										}`}
									>
										{bm.name}
									</button>
								))}
							</div>
						) : (
							<p className="text-xs text-muted-foreground">
								{t("pbNoBenchmarkSwim")}
							</p>
						)}
					</div>
				</div>
			)}

			{/* Pace Reference — for all swim types */}
			{newExercise.swim_type &&
				newExercise.swim_type !== "benchmark" &&
				benchmarkOptions.swim.length > 0 && (
					<div className="space-y-2 border border-dashed border-cyan-400/30 rounded-sm p-3">
						<span className="text-[10px] text-cyan-400 uppercase tracking-wider font-semibold">
							{t("pbPaceReference")}
						</span>
						<div className="grid grid-cols-2 gap-2">
							<div className="space-y-1">
								<span className="text-[10px] text-muted-foreground">
									{t("pbReferenceBenchmark")}
								</span>
								<select
									value={newExercise.pace_reference_benchmark_id || ""}
									onChange={(e) => {
										const bm = benchmarkOptions.swim.find(
											(b) => b.exercise_id === e.target.value,
										);
										setNewExercise({
											...newExercise,
											pace_reference_benchmark_id: e.target.value || null,
											pace_reference_benchmark_name: bm?.name || null,
										});
									}}
									className="w-full bg-secondary border border-border rounded-sm text-xs p-2"
								>
									<option value="">{t("noneOption")}</option>
									{benchmarkOptions.swim.map((bm) => (
										<option key={bm.exercise_id} value={bm.exercise_id}>
											{bm.name}
										</option>
									))}
								</select>
							</div>
							<div className="space-y-1">
								<span className="text-[10px] text-muted-foreground">
									{t("pbPacePercent")}
								</span>
								<Input
									type="number"
									min={1}
									max={200}
									value={newExercise.pace_reference_percentage ?? ""}
									onChange={(e) =>
										setNewExercise({
											...newExercise,
											pace_reference_percentage: e.target.value
												? Number(e.target.value)
												: null,
										})
									}
									className="bg-secondary border-transparent rounded-sm text-xs"
									placeholder={t("pbEg85")}
								/>
							</div>
						</div>
						{newExercise.pace_reference_benchmark_id &&
							newExercise.pace_reference_percentage && (
								<p className="text-[10px] text-cyan-400/70">
									{t("pbPaceRefAt")} {newExercise.pace_reference_percentage}
									{t("pbPaceRefOfPace")}{" "}
									{newExercise.pace_reference_benchmark_name || "benchmark"}
								</p>
							)}
					</div>
				)}

			{/* Notes — for all swim types */}
			{newExercise.swim_type && (
				<div className="space-y-1">
					<span className="text-[10px] text-muted-foreground uppercase tracking-wider">
						{t("notes")}
					</span>
					<Textarea
						value={newExercise.notes ?? ""}
						onChange={(e) =>
							setNewExercise({
								...newExercise,
								notes: e.target.value,
							})
						}
						className="bg-secondary border-transparent rounded-sm text-xs min-h-[48px] resize-none"
						placeholder={t("pbExtraNotesPlaceholder")}
					/>
				</div>
			)}
		</div>
	);
}
